/**
 * Platform auth client — thin fetch wrappers around `/api/v1/platform/auth/*`.
 *
 * Storage policy:
 *   - Access token + admin profile lives in localStorage under
 *     `hyder.platformToken` (Phase 4 #112: three-slot localStorage layout).
 *     This is INTENTIONALLY localStorage (not sessionStorage like the tenant
 *     token) — platform operators expect to come back to a previously opened
 *     console tab after a context switch. Refresh cookie is httpOnly +
 *     path-scoped to `/api/v1/platform/auth/` so a tenant page cannot send it.
 *   - The shape is `{ accessToken, expiresIn, tokenType, admin }`, matching
 *     `PlatformAuthSession` in `api/src/services/platformAuthService.ts`.
 *
 * MFA flow:
 *   - `login` → `{ data: { mfaRequired, mfaToken, expiresIn } }` OR
 *               `{ data: { mfaEnrolmentRequired, mfaToken, expiresIn } }`
 *   - `mfaVerify` (with the mfaToken + 6-digit code) → full session
 *   - `mfaEnrol` (with the enrolment mfaToken) → secret + otpauthUri + fresh mfaToken
 *   - `mfaVerifyEnrol` (with mfaToken + 6-digit code) → full session
 *
 * Auth client refactor (Phase 4): tenant-side `lib/api.ts` plus this module
 * share NO state. Each owns its own slot, so a stale tenant token cannot leak
 * into a platform request and vice versa.
 */

import { IMPERSONATION_TOKEN_KEY } from './impersonationToken';

export const PLATFORM_TOKEN_KEY = 'hyder.platformToken';

export type PlatformAdminRole =
  | 'platform_manager'
  | 'platform_readonly'
  | 'platform_support';

export type PlatformAdminProfile = {
  id: string;
  email: string;
  name: string;
  role: PlatformAdminRole;
};

export type PlatformSession = {
  accessToken: string;
  expiresIn: number;
  tokenType: 'Bearer';
  admin: PlatformAdminProfile;
};

export type PlatformMfaRequired = {
  mfaRequired: true;
  mfaToken: string;
  expiresIn: number;
};

export type PlatformMfaEnrolmentRequired = {
  mfaEnrolmentRequired: true;
  mfaToken: string;
  expiresIn: number;
};

export type PlatformLoginResponse =
  | { kind: 'session'; session: PlatformSession }
  | { kind: 'mfa-required'; mfaToken: string; expiresIn: number }
  | { kind: 'mfa-enrolment-required'; mfaToken: string; expiresIn: number };

export type PlatformMfaEnrolResponse = {
  factorId: string;
  type: 'totp';
  status: 'pending';
  secret: string;
  otpauthUri: string;
  mfaToken: string;
  expiresIn: number;
};

const isPlatformSessionShape = (v: unknown): v is PlatformSession => {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.accessToken === 'string' &&
    typeof o.expiresIn === 'number' &&
    o.tokenType === 'Bearer' &&
    typeof o.admin === 'object' &&
    o.admin !== null
  );
};

let cachedSession: PlatformSession | null = null;

export const setPlatformSession = (session: PlatformSession | null): void => {
  cachedSession = session;
  if (typeof window === 'undefined') return;
  if (session) {
    window.localStorage.setItem(PLATFORM_TOKEN_KEY, JSON.stringify(session));
  } else {
    window.localStorage.removeItem(PLATFORM_TOKEN_KEY);
  }
};

export const getPlatformSession = (): PlatformSession | null => {
  if (cachedSession) return cachedSession;
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(PLATFORM_TOKEN_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isPlatformSessionShape(parsed)) {
      window.localStorage.removeItem(PLATFORM_TOKEN_KEY);
      return null;
    }
    cachedSession = parsed;
    return cachedSession;
  } catch {
    window.localStorage.removeItem(PLATFORM_TOKEN_KEY);
    return null;
  }
};

const parseJson = async (res: Response): Promise<unknown> => {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const raiseError = (
  body: { error?: { message?: string; code?: string } } | null,
  fallback: string,
  status: number,
): never => {
  const message = body?.error?.message ?? `${fallback} (${status})`;
  const err = new Error(message) as Error & { code?: string; status?: number };
  if (body?.error?.code) err.code = body.error.code;
  err.status = status;
  throw err;
};

export const platformLogin = async (input: {
  email: string;
  password: string;
}): Promise<PlatformLoginResponse> => {
  const res = await fetch('/api/v1/platform/auth/login', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const body = (await parseJson(res)) as {
    data?: {
      mfaRequired?: true;
      mfaEnrolmentRequired?: true;
      mfaToken?: string;
      expiresIn?: number;
    };
    error?: { message?: string; code?: string };
  } | null;
  if (!res.ok || !body || !body.data) {
    raiseError(body, 'Platform sign-in failed', res.status);
  }
  const data = body!.data!;
  if (data.mfaRequired && data.mfaToken && typeof data.expiresIn === 'number') {
    return { kind: 'mfa-required', mfaToken: data.mfaToken, expiresIn: data.expiresIn };
  }
  if (
    data.mfaEnrolmentRequired &&
    data.mfaToken &&
    typeof data.expiresIn === 'number'
  ) {
    return {
      kind: 'mfa-enrolment-required',
      mfaToken: data.mfaToken,
      expiresIn: data.expiresIn,
    };
  }
  // No session path exists in current API (MFA mandatory) but keep type-safe.
  throw new Error('Unexpected platform login response');
};

const handleSessionResponse = async (res: Response): Promise<PlatformSession> => {
  const body = (await parseJson(res)) as {
    data?: unknown;
    error?: { message?: string; code?: string };
  } | null;
  if (!res.ok || !body || !isPlatformSessionShape(body.data)) {
    raiseError(body, 'Platform sign-in failed', res.status);
  }
  const session = body!.data as PlatformSession;
  setPlatformSession(session);
  return session;
};

export const platformMfaVerify = async (input: {
  mfaToken: string;
  code: string;
}): Promise<PlatformSession> => {
  const res = await fetch('/api/v1/platform/auth/mfa/verify', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return handleSessionResponse(res);
};

export const platformMfaEnrol = async (input: {
  mfaToken: string;
}): Promise<PlatformMfaEnrolResponse> => {
  const res = await fetch('/api/v1/platform/auth/mfa/enrol', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const body = (await parseJson(res)) as {
    data?: PlatformMfaEnrolResponse;
    error?: { message?: string; code?: string };
  } | null;
  if (!res.ok || !body || !body.data) {
    raiseError(body, 'MFA enrolment failed', res.status);
  }
  return body!.data!;
};

export const platformMfaVerifyEnrol = async (input: {
  mfaToken: string;
  code: string;
}): Promise<PlatformSession> => {
  const res = await fetch('/api/v1/platform/auth/mfa/verify-enrol', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return handleSessionResponse(res);
};

export const platformRefresh = async (): Promise<PlatformSession> => {
  const res = await fetch('/api/v1/platform/auth/refresh', {
    method: 'POST',
    credentials: 'include',
  });
  return handleSessionResponse(res);
};

export const platformLogout = async (): Promise<void> => {
  await fetch('/api/v1/platform/auth/logout', {
    method: 'POST',
    credentials: 'include',
  }).catch(() => undefined);
  setPlatformSession(null);
  // Defence-in-depth (#121): if a stale impersonation token still sits in
  // localStorage at logout time, clear it. The primary cleanup path is the
  // banner's [Exit] button; this guards the case where an operator logs out
  // mid-impersonation. We do NOT call the server-side impersonate DELETE
  // endpoint here — the marker row expires in 5 minutes and the cleanup
  // helper prunes it, and at logout time we may not even have a live
  // impersonation session to authenticate the call with.
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
  }
};

// ---------------------------------------------------------------------------
// PUBLIC platform-admin invitation accept surface (#109 Phase 2). No bearer —
// the single-use token in the URL is the authorisation. Mirrors the tenant
// `lib/invitationClient.ts`.
// ---------------------------------------------------------------------------

export type PlatformInvitationProbeStatus = 'valid' | 'expired' | 'used';

export type PlatformInvitationProbe = {
  email: string;
  status: PlatformInvitationProbeStatus;
};

/** Discriminated accept outcome — mirrors the API's session / MFA legs. */
export type PlatformAcceptInviteResult =
  | { kind: 'session'; session: PlatformSession }
  | { kind: 'mfa-required'; mfaToken: string; expiresIn: number }
  | { kind: 'mfa-enrolment-required'; mfaToken: string; expiresIn: number };

// ---------------------------------------------------------------------------
// PUBLIC platform-admin self-service password reset. No bearer — the email +
// the single-use token are the only inputs. Mirrors the tenant
// `lib/passwordResetClient.ts`.
// ---------------------------------------------------------------------------

/**
 * Request a platform reset link. The API ALWAYS returns 200 with a generic
 * message (anti-enumeration), so this resolves on success and only throws on a
 * transport / rate-limit error.
 */
export const platformForgotPassword = async (email: string): Promise<void> => {
  const res = await fetch('/api/v1/platform/auth/forgot-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const body = (await parseJson(res)) as {
      error?: { message?: string; code?: string };
    } | null;
    raiseError(body, 'Could not request a password reset', res.status);
  }
};

/** Set a new platform password with a single-use token. Throws on bad tokens. */
export const platformResetPassword = async (
  token: string,
  password: string,
): Promise<void> => {
  const res = await fetch('/api/v1/platform/auth/reset-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ token, password }),
  });
  if (!res.ok) {
    const body = (await parseJson(res)) as {
      error?: { message?: string; code?: string };
    } | null;
    raiseError(body, 'Could not reset your password', res.status);
  }
};

export const platformProbeInvitation = async (
  token: string,
): Promise<PlatformInvitationProbe> => {
  const res = await fetch(
    `/api/v1/platform/auth/invitations/${encodeURIComponent(token)}`,
    { headers: { Accept: 'application/json' } },
  );
  const body = (await parseJson(res)) as {
    data?: PlatformInvitationProbe;
    error?: { message?: string; code?: string };
  } | null;
  if (!res.ok || !body || !body.data) {
    raiseError(body, 'Could not load this invitation', res.status);
  }
  return body!.data!;
};

export const platformAcceptInvitation = async (
  token: string,
  password: string,
): Promise<PlatformAcceptInviteResult> => {
  const res = await fetch(
    `/api/v1/platform/auth/invitations/${encodeURIComponent(token)}/accept`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ password }),
    },
  );
  const body = (await parseJson(res)) as {
    data?: {
      mfaRequired?: true;
      mfaEnrolmentRequired?: true;
      mfaToken?: string;
      expiresIn?: number;
    } & Partial<PlatformSession>;
    error?: { message?: string; code?: string };
  } | null;
  if (!res.ok || !body || !body.data) {
    raiseError(body, 'Could not activate your account', res.status);
  }
  const data = body!.data!;
  if (data.mfaRequired && data.mfaToken && typeof data.expiresIn === 'number') {
    return { kind: 'mfa-required', mfaToken: data.mfaToken, expiresIn: data.expiresIn };
  }
  if (data.mfaEnrolmentRequired && data.mfaToken && typeof data.expiresIn === 'number') {
    return {
      kind: 'mfa-enrolment-required',
      mfaToken: data.mfaToken,
      expiresIn: data.expiresIn,
    };
  }
  // Session leg — defence-in-depth; platform MFA is mandatory so this is not
  // reachable today, but persist it if a bare session is ever issued.
  if (isPlatformSessionShape(data)) {
    setPlatformSession(data);
    return { kind: 'session', session: data };
  }
  throw new Error('Unexpected platform invitation accept response');
};
